import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel
} from '@headlessui/react';
import dayjs from 'dayjs';
import { ChevronDownIcon } from 'lucide-react';
import type { ElementType } from 'react';
import React from 'react';
import { IGetPointsHistoryResponse } from '../types/tiers.types';

const disclosureConfig = {
  className: 'p-0 bg-gray-50',
  as: 'div' as ElementType,
  defaultOpen: false
};

const AllPointsHistory: React.FC<{
  pointsData: IGetPointsHistoryResponse['data'];
}> = ({ pointsData }) => {
  return (
    <Disclosure {...disclosureConfig}>
      <DisclosureButton className="group flex w-full items-center justify-between p-6 py-3">
        <span className="text-sm/6 font-medium">All</span>
        <ChevronDownIcon className="size-5 group-data-[open]:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-2 text-sm/5">
        {pointsData.map((transaction, index) => (
          <TransactionDetails key={index} {...transaction} />
        ))}
      </DisclosurePanel>
    </Disclosure>
  );
};

const ReceivedPointsHistory: React.FC<{
  pointsData: IGetPointsHistoryResponse['data'];
}> = ({ pointsData }) => {
  return (
    <Disclosure {...disclosureConfig}>
      <DisclosureButton className="group flex w-full items-center justify-between p-6 py-3">
        <span className="text-sm/6 font-medium">Received</span>
        <ChevronDownIcon className="size-5 fill-white/60 group-data-[open]:rotate-180 group-data-[hover]:fill-white/50" />
      </DisclosureButton>
      <DisclosurePanel className="mt-2 text-sm/5 text-white/50">
        {pointsData
          .filter((t) => t.transactionType === 'earn')
          .map((transaction, index) => (
            <TransactionDetails key={index} {...transaction} />
          ))}
      </DisclosurePanel>
    </Disclosure>
  );
};

const UsedPointsHistory: React.FC<{
  pointsData: IGetPointsHistoryResponse['data'];
}> = ({ pointsData }) => {
  return (
    <Disclosure {...disclosureConfig}>
      <DisclosureButton className="group flex w-full items-center justify-between p-6 py-3">
        <span className="text-sm/6 font-medium">Used</span>
        <ChevronDownIcon className="size-5 fill-white/60 group-data-[open]:rotate-180 group-data-[hover]:fill-white/50" />
      </DisclosureButton>
      <DisclosurePanel className="mt-2 text-sm/5 text-white/50">
        {pointsData
          .filter((t) => t.transactionType === 'redeem')
          .map((transaction, index) => (
            <TransactionDetails key={index} {...transaction} />
          ))}
      </DisclosurePanel>
    </Disclosure>
  );
};

const TransactionDetails = ({
  reason = '',
  date,
  points,
  branch,
  transactionType
}: IGetPointsHistoryResponse['data'][number]) => {
  return (
    <div className="flex h-14 items-center bg-white p-6 py-3">
      <div className="flex flex-1 flex-col">
        <p className={'font-normal text-gray-600'}>
          {reason?.includes('REFUND_CURR_')
            ? 'Refund Completed'
            : 'Transaction Completed'}
        </p>
        <p className={'flex items-center gap-x-2 text-sm text-gray-400'}>
          <span>{dayjs(date).format('D MMM YYYY HH:mm')}</span>
          <span>{branch ? ' • ' + branch : ''}</span>
        </p>
      </div>
      {transactionType === 'earn' ? (
        <p className="text-md font-semibold text-green-500">{'+' + points}</p>
      ) : (
        <p className="text-md font-semibold text-gray-800">
          {(reason?.includes('REFUND_CURR_') ? '' : '-') + points}
        </p>
      )}
    </div>
  );
};

export { AllPointsHistory, ReceivedPointsHistory, UsedPointsHistory };
